// Central API client with environment-driven base URL
import { getEntraAccessToken } from './authService';

export function getApiBaseUrl(): string {
  // In development: leave empty so requests use relative paths through the
  // webpack dev-server proxy (setupProxy.js).  This avoids mixed-content
  // issues when the frontend is served over HTTPS (Office Add-in).
  // In production: REACT_APP_API_BASE_URL must be set to the backend origin.
  const raw = process.env.REACT_APP_API_BASE_URL || '';
  return raw.replace(/\/$/, '');
}

export async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const base = getApiBaseUrl();
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  const response = await fetch(url, init);
  const data = await safeJson(response);
  if (!response.ok) {
    const message = (data && (data.detail || data.error)) || `${response.status} ${response.statusText}`;
    throw new Error(message);
  }
  return data as T;
}

export async function httpJson<T>(path: string, body?: any, init?: RequestInit): Promise<T> {
  return http<T>(path, {
    method: body ? 'POST' : (init?.method || 'GET'),
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    body: body ? JSON.stringify(body) : undefined,
    ...init,
  });
}

async function safeJson(res: Response): Promise<any | null> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * HTTP client with automatic authentication
 *
 * Makes authenticated API calls with automatic JWT token handling.
 * Automatically adds Authorization header and handles 401 errors.
 *
 * @param path - API endpoint path (relative or absolute URL)
 * @param init - Optional fetch options
 * @returns Promise<T> - Parsed JSON response
 * @throws Error with 'UNAUTHORIZED' message on 401 (caught by global handler)
 *
 * @example
 * ```typescript
 * const data = await httpWithAuth<UserResponse>('/api/user/profile');
 * const result = await httpWithAuth<CreateResponse>('/api/threads', {
 *   method: 'POST',
 *   body: JSON.stringify({ message: 'Hello' })
 * });
 * ```
 */
export async function httpWithAuth<T>(path: string, init?: RequestInit): Promise<T> {
  const base = getApiBaseUrl();
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;

  // Prepare headers with authentication
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Merge existing headers if provided
  if (init?.headers) {
    const existingHeaders = new Headers(init.headers);
    existingHeaders.forEach((value, key) => {
      headers[key] = value;
    });
  }

  // Get Entra External ID token (required for authenticated endpoints)
  const token = await getEntraAccessToken();

  // Token is required for authenticated endpoints
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }

  // Add Authorization header
  headers['Authorization'] = `Bearer ${token}`;

  // Execute fetch request
  const response = await fetch(url, {
    ...init,
    headers,
  });

  // Parse response
  const data = await safeJson(response);

  // Handle errors
  if (!response.ok) {
    // Special handling for 401 Unauthorized
    if (response.status === 401) {
      // This will be caught by the global AuthInterceptor handler
      throw new Error('UNAUTHORIZED');
    }

    // Other errors
    const message = (data && (data.detail || data.error)) || `${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  return data as T;
}
